import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  DropdownSingle,
  SwitchSingle,
  icons,
  colors2,
  UI_BODY_SM_DF,
  UI_BODY_SM,
} from "oolib";
import { SidebarLayoutV2 } from "@/components/layout/SidebarLayoutV2";
import { DrilldownList } from "@/components/UI/DrilldownList";
import { JsonPreview } from "../FiltersPlayground/components/JsonPreview";
import {
  SidebarSection,
  DescriptionText,
} from "../FiltersPlayground/components";
import {
  StyledSidebarStack,
  StyledLayoutWrapper,
  StyledPanel,
} from "../FiltersPlayground/styled";
import {
  CATEGORY_OPTIONS,
  ITEMS_MAP,
  SETTINGS_META,
  getSettingsChildOptions,
  REGION_OPTIONS,
  COUNTRY_OPTIONS,
  CITY_OPTIONS,
  THEME_META_OPTIONS,
  LAZY_THEMES,
  LAZY_SUBTHEMES,
  LAZY_TAGS,
  NESTED_TREE_OPTIONS,
  DATASET_OPTIONS,
  FOLDER_MODE_OPTIONS,
} from "./configs";
import { StaticDrilldownList } from "@/components/UI/DrilldownList/comps/StaticDrilldownList";

const { CaretDown } = icons;

// ─── Toggle helper ──────────────────────────────────

const toggleOption = (prev, key, option) => {
  const current = prev[key] || [];
  const exists = current.some((v) => v.value === option.value);
  if (exists) {
    return { ...prev, [key]: current.filter((v) => v.value !== option.value) };
  }
  return { ...prev, [key]: [...current, option] };
};

// ─── Simulated lazy-load hook ───────────────────────

const useLazyData = (dataMap, key, delay = 600) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!key) {
      setData([]);
      return;
    }
    setIsLoading(true);
    timerRef.current = setTimeout(() => {
      setData(dataMap[key] || []);
      setIsLoading(false);
    }, delay);
    return () => clearTimeout(timerRef.current);
  }, [key, dataMap, delay]);

  return { data, isLoading };
};

// ─── Dataset hooks ──────────────────────────────────

const useTreeDataset = () => {
  const [values, setValues] = useState([]);

  const onChange = useCallback(
    (opt) =>
      setValues((prev) => {
        const exists = prev.some((v) => v.value === opt.value);
        if (exists) return prev.filter((v) => v.value !== opt.value);
        return [...prev, { value: opt.value, display: opt.display }];
      }),
    []
  );

  const rootProps = useMemo(
    () => ({
      options: NESTED_TREE_OPTIONS,
      value: values,
      isMulti: true,
      onChange,
    }),
    [values, onChange]
  );

  return { rootProps, values, isTree: true };
};

const useLinearDataset = () => {
  const [drilled, setDrilled] = useState(null);
  const [values, setValues] = useState({ categories: [], items: [] });

  const onChangeRoot = useCallback(
    (opt) => setValues((p) => toggleOption(p, "categories", opt)),
    []
  );
  const onChangeChild = useCallback(
    (opt) => setValues((p) => toggleOption(p, "items", opt)),
    []
  );

  const rootProps = useMemo(
    () => ({
      options: CATEGORY_OPTIONS,
      value: values.categories,
      isMulti: true,
      onChange: onChangeRoot,
      onDrilldown: (opt) => setDrilled(opt.value),
      getChildLevel: () => ({
        options: drilled ? ITEMS_MAP[drilled] || [] : [],
        value: values.items,
        isMulti: true,
        onChange: onChangeChild,
      }),
    }),
    [values, drilled, onChangeRoot, onChangeChild]
  );

  return { rootProps, values };
};

const useBranchingDataset = () => {
  const [values, setValues] = useState({});

  const makeOnChange = useCallback(
    (key) => (opt) => setValues((p) => toggleOption(p, key, opt)),
    []
  );

  const rootProps = useMemo(
    () => ({
      options: SETTINGS_META,
      isMulti: false,
      getChildLevel: (meta) => ({
        options: getSettingsChildOptions(meta.value),
        value: values[meta.value] || [],
        isMulti: true,
        onChange: makeOnChange(meta.value),
      }),
    }),
    [values, makeOnChange]
  );

  return { rootProps, values };
};

const useDeepDataset = () => {
  const [drilledRegion, setDrilledRegion] = useState(null);
  const [drilledCountry, setDrilledCountry] = useState(null);
  const [values, setValues] = useState({ regions: [], countries: [], cities: [] });

  const onChangeRegions = useCallback(
    (opt) => setValues((p) => toggleOption(p, "regions", opt)),
    []
  );
  const onChangeCountries = useCallback(
    (opt) => setValues((p) => toggleOption(p, "countries", opt)),
    []
  );
  const onChangeCities = useCallback(
    (opt) => setValues((p) => toggleOption(p, "cities", opt)),
    []
  );

  const countryOptions = useMemo(
    () => (drilledRegion ? COUNTRY_OPTIONS[drilledRegion] || [] : []),
    [drilledRegion]
  );
  const cityOptions = useMemo(
    () => (drilledCountry ? CITY_OPTIONS[drilledCountry] || [] : []),
    [drilledCountry]
  );

  const rootProps = useMemo(
    () => ({
      options: REGION_OPTIONS,
      value: values.regions,
      isMulti: true,
      onChange: onChangeRegions,
      onDrilldown: (opt) => {
        setDrilledRegion(opt.value);
        setDrilledCountry(null);
      },
      getChildLevel: () => ({
        options: countryOptions,
        value: values.countries,
        isMulti: true,
        onChange: onChangeCountries,
        onDrilldown: (opt) => setDrilledCountry(opt.value),
        getChildLevel: () => ({
          options: cityOptions,
          value: values.cities,
          isMulti: true,
          onChange: onChangeCities,
        }),
      }),
    }),
    [values, countryOptions, cityOptions, onChangeRegions, onChangeCountries, onChangeCities]
  );

  return { rootProps, values };
};

const useLazyDataset = () => {
  const [openLevel, setOpenLevel] = useState(null);
  const [drilledTheme, setDrilledTheme] = useState(null);
  const [drilledSubTheme, setDrilledSubTheme] = useState(null);
  const [values, setValues] = useState({ themes: [], subThemes: [], tags: [] });

  const themes = useLazyData(
    { all: LAZY_THEMES },
    openLevel === "themes" ? "all" : null
  );
  const subThemes = useLazyData(LAZY_SUBTHEMES, drilledTheme);
  const tags = useLazyData(LAZY_TAGS, drilledSubTheme);

  const onChangeThemes = useCallback(
    (opt) => setValues((p) => toggleOption(p, "themes", opt)),
    []
  );
  const onChangeSubThemes = useCallback(
    (opt) => setValues((p) => toggleOption(p, "subThemes", opt)),
    []
  );
  const onChangeTags = useCallback(
    (opt) => setValues((p) => toggleOption(p, "tags", opt)),
    []
  );

  const rootProps = useMemo(
    () => ({
      options: THEME_META_OPTIONS,
      isMulti: false,
      onDrilldown: (entry) => {
        setOpenLevel(entry.value);
        setDrilledTheme(null);
        setDrilledSubTheme(null);
      },
      getChildLevel: (meta) => {
        if (meta.value === "themes") {
          return {
            options: themes.data,
            value: values.themes,
            isMulti: true,
            isLoading: themes.isLoading,
            onChange: onChangeThemes,
            onDrilldown: (opt) => {
              setDrilledTheme(opt.value);
              setDrilledSubTheme(null);
            },
            getChildLevel: () => ({
              options: subThemes.data,
              value: values.subThemes,
              isMulti: true,
              isLoading: subThemes.isLoading,
              onChange: onChangeSubThemes,
              onDrilldown: (opt) => setDrilledSubTheme(opt.value),
              getChildLevel: () => ({
                options: tags.data,
                value: values.tags,
                isMulti: true,
                isLoading: tags.isLoading,
                onChange: onChangeTags,
              }),
            }),
          };
        }
        if (meta.value === "subThemes") {
          return {
            options: subThemes.data,
            value: values.subThemes,
            isMulti: true,
            isLoading: subThemes.isLoading,
            onChange: onChangeSubThemes,
            onDrilldown: (opt) => setDrilledSubTheme(opt.value),
            getChildLevel: () => ({
              options: tags.data,
              value: values.tags,
              isMulti: true,
              isLoading: tags.isLoading,
              onChange: onChangeTags,
            }),
          };
        }
        if (meta.value === "tags") {
          return {
            options: tags.data,
            value: values.tags,
            isMulti: true,
            isLoading: tags.isLoading,
            onChange: onChangeTags,
          };
        }
        return null;
      },
    }),
    [values, themes, subThemes, tags, onChangeThemes, onChangeSubThemes, onChangeTags]
  );

  return { rootProps, values };
};

// ─── Map dataset key to hook ────────────────────────

const useActiveDataset = (datasetKey) => {
  const tree = useTreeDataset();
  const linear = useLinearDataset();
  const branching = useBranchingDataset();
  const deep = useDeepDataset();
  const lazy = useLazyDataset();

  const map = { tree, linear, branching, deep, lazy };
  return map[datasetKey] || tree;
};

// ─── Dropdown trigger ───────────────────────────────

const DropdownTrigger = ({ label = "Select..." }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.8rem 1rem",
      border: `1px solid ${colors2.grey10}`,
      borderRadius: "0.6rem",
      cursor: "pointer",
      backgroundColor: "white",
    }}
  >
    <UI_BODY_SM_DF style={{ color: colors2.grey60 }}>{label}</UI_BODY_SM_DF>
    <CaretDown size={16} color={colors2.grey40} />
  </div>
);

// ─── Main Component ────────────────────────────────

export const DrilldownListPlayground = () => {
  const [datasetKey, setDatasetKey] = useState("tree");
  const [folderMode, setFolderMode] = useState("exclusive");
  const [useDropdown, setUseDropdown] = useState(false);

  const { rootProps, values, isTree } = useActiveDataset(datasetKey);

  return (
    <SidebarLayoutV2
      rsbWidth={340}
      rsbAllowCollapse
      rsbAllowResize
      toggleState={{ rsb: true }}
      rsbComp={
        <StyledSidebarStack style={{ padding: "1rem" }}>
          <SidebarSection
            title="Dataset"
            description="Switch between different hierarchy structures."
          >
            <DropdownSingle
              id="dataset-picker"
              value={datasetKey}
              onChange={(_id, v) => typeof v === "string" && setDatasetKey(v)}
              options={DATASET_OPTIONS}
              saveValueAsString
            />
          </SidebarSection>

          <SidebarSection
            title="Props"
            description="Toggle DrilldownList props."
          >
            <DropdownSingle
              id="folder-mode-picker"
              label="folderSelectionMode"
              value={folderMode}
              onChange={(_id, v) => typeof v === "string" && setFolderMode(v)}
              options={FOLDER_MODE_OPTIONS}
              saveValueAsString
            />
            <SwitchSingle
              id="use-dropdown"
              option={{ display: "genCustomSelectComp", value: true }}
              value={useDropdown ? { display: "genCustomSelectComp", value: true } : undefined}
              onChange={(_id, v) => setUseDropdown(Boolean(v))}
              layoutStyle="style2"
            />
          </SidebarSection>

          <SidebarSection title="Selected values">
            <JsonPreview data={values} collapseDepth={2} />
          </SidebarSection>
        </StyledSidebarStack>
      }
    >
      <StyledLayoutWrapper>
        <StyledPanel>
          <UI_BODY_SM semibold>DrilldownList</UI_BODY_SM>
          <DescriptionText>
            {DATASET_OPTIONS.find((d) => d.value === datasetKey)?.display}
            {" — "}
            folderSelectionMode: {folderMode}
            {useDropdown ? " — dropdown mode" : ""}
          </DescriptionText>
          {isTree ? (
            <StaticDrilldownList
              {...rootProps}
              folderSelectionMode={folderMode}
              genCustomSelectComp={
                useDropdown
                  ? () => <DropdownTrigger label="Click to open drilldown..." />
                  : undefined
              }
            />
          ) : (
            <DrilldownList
              {...rootProps}
              folderSelectionMode={folderMode}
              genCustomSelectComp={
                useDropdown
                  ? () => <DropdownTrigger label="Click to open drilldown..." />
                  : undefined
              }
            />
          )}
        </StyledPanel>
      </StyledLayoutWrapper>
    </SidebarLayoutV2>
  );
};
